/**
 * BootcampView — the 8-week Property Development program UI.
 *
 * States
 * ------
 * - **Not enrolled** — renders the program splash + "Start my benchmark" CTA.
 * - **Benchmarking** — renders the BenchmarkWizard inline. No weeks visible yet.
 * - **Active** — renders the vertical timeline with locked / current / passed
 *   states; clicking an unlocked week opens the detail modal.
 * - **Completed** — same timeline with a completion banner + evaluation review.
 *
 * Design
 * ------
 * Uses the existing UI primitives (Card, Button, EmptyState, ProgressBar,
 * Skeleton) and the ToastContext for async feedback. No new design tokens,
 * no new animation library. Respects `prefers-reduced-motion`.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Award,
    CheckCircle2,
    ChevronRight,
    Flag,
    Lock,
    PlayCircle,
    RefreshCw,
    Rocket,
} from 'lucide-react';
import Button from '../ui/Button';
import Card from '../ui/Card';
import EmptyState from '../ui/EmptyState';
import ProgressBar from '../ui/ProgressBar';
import { Skeleton } from '../Skeleton';
import { useToastContext } from '../ToastProvider';
import {
    getBootcampProgram,
    enrollInBootcamp,
    submitBenchmark,
    submitEvaluation,
    type BenchmarkPayload,
    type BootcampProgram,
    type BootcampWeekSummary,
    type EvaluationPayload,
} from '../../bootcampService';
import BenchmarkWizard from './BenchmarkWizard';
import BootcampAdminDashboard from './BootcampAdminDashboard';
import BootcampWeekDetail from './BootcampWeekDetail';
import EvaluationWizard from './EvaluationWizard';
import {
    fetchCurrentUserWithPlan,
    isAdmin,
    type BackendUserWithPlan,
} from '../../userService';
import { UserCheck, GraduationCap } from 'lucide-react';
import './BootcampView.css';

function BootcampView() {
    const [program, setProgram] = useState<BootcampProgram | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [enrollBusy, setEnrollBusy] = useState(false);
    const [openWeek, setOpenWeek] = useState<number | null>(null);
    const [currentUser, setCurrentUser] = useState<BackendUserWithPlan | null>(null);
    const [adminMode, setAdminMode] = useState(false);
    const { success, error: toastError } = useToastContext();

    useEffect(() => {
        void fetchCurrentUserWithPlan().then(setCurrentUser).catch(() => {
            // Non-fatal — the admin toggle simply won't appear.
        });
    }, []);

    const isAdminUser = isAdmin(currentUser);

    // Re-render every minute so "Unlocks in 2h 14m" countdowns on
    // scheduled weeks stay live. When a countdown reaches zero we also
    // refetch the program so the backend's lazy unlock fires and the
    // week flips from scheduled → unlocked.
    const [nowTick, setNowTick] = useState(0);
    const refetchTimerRef = useRef<number | null>(null);
    useEffect(() => {
        const id = window.setInterval(() => setNowTick((n) => n + 1), 60_000);
        return () => window.clearInterval(id);
    }, []);

    const refresh = useCallback(async () => {
        setError(null);
        try {
            const data = await getBootcampProgram();
            setProgram(data);
        } catch (err) {
            console.error('[BootcampView] failed to load program', err);
            setError("Couldn't load the bootcamp program. Please try again.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const handleEnroll = useCallback(async () => {
        setEnrollBusy(true);
        try {
            await enrollInBootcamp();
            success('Enrollment started. Complete your benchmark to unlock Week 1.');
            await refresh();
        } catch (err) {
            console.error('[BootcampView] enroll failed', err);
            toastError("Couldn't start enrollment. Please try again.");
        } finally {
            setEnrollBusy(false);
        }
    }, [refresh, success, toastError]);

    const handleEvaluationSubmit = useCallback(async (payload: EvaluationPayload) => {
        try {
            await submitEvaluation(payload);
            success('Program complete — your certificate is on its way.');
            await refresh();
        } catch (err) {
            console.error('[BootcampView] evaluation submit failed', err);
            const msg = err instanceof Error ? err.message : 'Evaluation submission failed.';
            toastError(msg);
            throw err; // let EvaluationWizard clear its saving state
        }
    }, [refresh, success, toastError]);

    const handleBenchmarkSubmit = useCallback(async (payload: BenchmarkPayload) => {
        try {
            await submitBenchmark(payload);
            success('Benchmark submitted. Week 1 is now unlocked.');
            await refresh();
        } catch (err) {
            console.error('[BootcampView] benchmark submit failed', err);
            const msg = err instanceof Error ? err.message : 'Benchmark submission failed.';
            toastError(msg);
            throw err; // let BenchmarkWizard clear its saving state
        }
    }, [refresh, success, toastError]);

    // ── Overall progress percentage ────────────────────────────────────
    const progressPct = useMemo(() => {
        if (!program || !program.enrollment) return 0;
        const total = program.weeks.length || 1;
        const passed = program.weeks.filter(
            (w) => w.progress && w.progress.pass_status === 'pass',
        ).length;
        return (passed / total) * 100;
    }, [program]);

    // Auto-refetch when any scheduled week's countdown is about to
    // elapse — catches the transition from "scheduled" to "unlocked"
    // without waiting for the next minute tick.
    useEffect(() => {
        if (!program || !program.enrollment) return;
        if (refetchTimerRef.current !== null) {
            window.clearTimeout(refetchTimerRef.current);
            refetchTimerRef.current = null;
        }
        const scheduled = program.weeks
            .map((w) => w.progress)
            .filter((p): p is NonNullable<typeof p> =>
                !!p && !p.unlocked && p.seconds_until_available !== null,
            );
        if (scheduled.length === 0) return;
        const nextSeconds = Math.min(...scheduled.map((p) => p.seconds_until_available ?? Infinity));
        if (!isFinite(nextSeconds) || nextSeconds <= 0) return;
        // Fire 1s after the countdown hits zero so the backend's lazy
        // unlock has the clock ticked over.
        refetchTimerRef.current = window.setTimeout(() => {
            void refresh();
        }, (nextSeconds + 1) * 1000);
        return () => {
            if (refetchTimerRef.current !== null) {
                window.clearTimeout(refetchTimerRef.current);
                refetchTimerRef.current = null;
            }
        };
    }, [program, refresh]);

    // ── Loading / error states ─────────────────────────────────────────
    if (loading) {
        return (
            <div className="bootcamp-view">
                <div className="bootcamp-view__skeletons">
                    <Skeleton width="100%" height="160px" borderRadius="16px" />
                    <Skeleton width="100%" height="96px" borderRadius="12px" />
                    <Skeleton width="100%" height="96px" borderRadius="12px" />
                    <Skeleton width="100%" height="96px" borderRadius="12px" />
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bootcamp-view">
                <EmptyState
                    icon={<RefreshCw size={26} />}
                    title="Couldn't load bootcamp"
                    description={error}
                    action={
                        <Button variant="secondary" size="md" onClick={() => void refresh()}>
                            Try again
                        </Button>
                    }
                />
            </div>
        );
    }

    if (!program) {
        return (
            <div className="bootcamp-view">
                <EmptyState
                    icon={<Rocket size={26} />}
                    title="Bootcamp not configured"
                    description="The program has not been seeded yet. Check back shortly."
                />
            </div>
        );
    }

    const enrollment = program.enrollment;
    const isEnrolled = enrollment !== null;
    const isBenchmarking = enrollment?.status === 'benchmarking';
    const isActive = enrollment?.status === 'active';
    const isCompleted = enrollment?.status === 'completed';
    // Evaluation unlocks the moment every week has been passed. The
    // backend refuses the submission if any week is unpassed, but the
    // UI needs to gate the wizard on the same condition so we don't
    // show it prematurely.
    const allWeeksPassed =
        isActive &&
        program.weeks.length > 0 &&
        program.weeks.every(
            (w) => w.progress && w.progress.pass_status === 'pass',
        );

    if (isAdminUser && adminMode) {
        return (
            <div className="bootcamp-view">
                <div className="bootcamp-view__admin-toggle">
                    <Button
                        variant="secondary"
                        size="sm"
                        icon={<GraduationCap size={16} />}
                        onClick={() => setAdminMode(false)}
                    >
                        View as student
                    </Button>
                </div>
                <BootcampAdminDashboard />
            </div>
        );
    }

    return (
        <div className="bootcamp-view">
            {isAdminUser && (
                <div className="bootcamp-view__admin-toggle">
                    <Button
                        variant="secondary"
                        size="sm"
                        icon={<UserCheck size={16} />}
                        onClick={() => setAdminMode(true)}
                    >
                        Facilitator review queue
                    </Button>
                </div>
            )}
            {/* ── Hero ──────────────────────────────────────────────── */}
            <header className="bootcamp-view__hero">
                <div className="bootcamp-view__hero-body">
                    <div className="bootcamp-view__hero-badge">
                        <Rocket size={16} aria-hidden="true" />
                        {program.week_count}-week program
                    </div>
                    <h1 className="bootcamp-view__title">{program.title}</h1>
                    <p className="bootcamp-view__tagline">{program.tagline}</p>
                    <p className="bootcamp-view__mission">{program.mission}</p>

                    {isEnrolled && (
                        <div className="bootcamp-view__progress">
                            <div className="bootcamp-view__progress-label">
                                Program progress
                            </div>
                            <ProgressBar value={progressPct} showLabel ariaLabel="Program progress" />
                        </div>
                    )}

                    {!isEnrolled && (
                        <Button
                            variant="primary"
                            size="lg"
                            icon={<Flag size={18} />}
                            onClick={handleEnroll}
                            disabled={enrollBusy}
                        >
                            {enrollBusy ? 'Starting…' : 'Start my benchmark'}
                        </Button>
                    )}
                </div>
            </header>

            {/* ── Benchmark wizard ──────────────────────────────────── */}
            {isBenchmarking && (
                <BenchmarkWizard onSubmit={handleBenchmarkSubmit} />
            )}

            {/* ── Final evaluation (every week passed, not yet
                submitted) ──────────────────────────────────────────── */}
            {allWeeksPassed && (
                <EvaluationWizard onSubmit={handleEvaluationSubmit} />
            )}

            {/* ── Completion banner ─────────────────────────────────── */}
            {isCompleted && (
                <Card className="bootcamp-view__completion">
                    <Card.Header>
                        <div className="bootcamp-view__completion-icon" aria-hidden="true">
                            <Award size={28} />
                        </div>
                        <div>
                            <h2>Congratulations — program complete!</h2>
                            <p>
                                You've earned the Property Development Bootcamp certificate.
                                Your alumni network access is active.
                            </p>
                        </div>
                    </Card.Header>
                </Card>
            )}

            {/* ── Timeline ──────────────────────────────────────────── */}
            {(isActive || isCompleted) && (
                <section className="bootcamp-view__timeline" aria-label="Program weeks">
                    <h2 className="bootcamp-view__timeline-title">The 8-week journey</h2>
                    <ol className="bootcamp-view__weeks">
                        {program.weeks.map((week) => (
                            <WeekRow
                                key={week.id}
                                week={week}
                                nowTick={nowTick}
                                onOpen={() => setOpenWeek(week.week_number)}
                            />
                        ))}
                    </ol>
                </section>
            )}

            {/* ── Week detail modal ─────────────────────────────────── */}
            {openWeek !== null && (
                <BootcampWeekDetail
                    weekNumber={openWeek}
                    onClose={() => setOpenWeek(null)}
                    onChanged={() => {
                        void refresh();
                    }}
                />
            )}
        </div>
    );
}

// ── Week row ────────────────────────────────────────────────────────────

type WeekRowProps = {
    week: BootcampWeekSummary;
    nowTick: number;
    onOpen: () => void;
};

function WeekRow({ week, nowTick, onOpen }: WeekRowProps) {
    // ``nowTick`` is only here to force a re-render each minute — it
    // doesn't change the computation result, which reads Date.now()
    // directly inside the helpers below.
    void nowTick;
    const status = weekStatus(week);
    const locked = !week.progress || !week.progress.unlocked;
    const scheduledText = formatScheduledUnlock(week.progress);
    return (
        <li className={`bootcamp-view__week bootcamp-view__week--${status}`}>
            <Card
                variant="row"
                interactive={!locked}
                className="bootcamp-view__week-card"
                onClick={locked ? undefined : onOpen}
            >
                <Card.Header>
                    <div className="bootcamp-view__week-number" aria-hidden="true">
                        {locked ? <Lock size={18} /> : status === 'passed' ? <CheckCircle2 size={18} /> : <PlayCircle size={18} />}
                        <span>{week.week_number}</span>
                    </div>
                    <div className="bootcamp-view__week-body">
                        <h3 className="bootcamp-view__week-title">{week.course_title}</h3>
                        {week.theme && (
                            <p className="bootcamp-view__week-theme">{week.theme}</p>
                        )}
                        <div className="bootcamp-view__week-meta">
                            <span>{week.lessons_count} lessons</span>
                            {week.estimated_hours > 0 && (
                                <span>~{week.estimated_hours}h</span>
                            )}
                            <WeekBadge status={status} />
                        </div>
                        {scheduledText && status === 'scheduled' && (
                            <p className="bootcamp-view__week-scheduled">
                                {scheduledText}
                            </p>
                        )}
                    </div>
                    {!locked && (
                        <ChevronRight size={18} className="bootcamp-view__week-chevron" aria-hidden="true" />
                    )}
                </Card.Header>
            </Card>
        </li>
    );
}

function WeekBadge({ status }: { status: WeekStatus }) {
    const label = {
        locked: 'Locked',
        scheduled: 'Scheduled',
        unlocked: 'Ready',
        in_progress: 'In progress',
        submitted: 'Awaiting review',
        retry: 'Retry requested',
        passed: 'Passed',
    }[status];
    return <span className={`bootcamp-view__week-badge bootcamp-view__week-badge--${status}`}>{label}</span>;
}

type WeekStatus = 'locked' | 'scheduled' | 'unlocked' | 'in_progress' | 'submitted' | 'retry' | 'passed';

/** Compute the live seconds remaining until a week becomes available.
 *  Uses ``available_from`` (absolute ISO timestamp) instead of the
 *  stale ``seconds_until_available`` that ships with the API response
 *  so the countdown updates every minute without a refetch. */
function computeSecondsRemaining(
    progress: BootcampWeekSummary['progress'],
): number | null {
    if (!progress || progress.unlocked) return null;
    if (!progress.available_from) return null;
    const target = Date.parse(progress.available_from);
    if (Number.isNaN(target)) return null;
    const diff = Math.max(0, Math.floor((target - Date.now()) / 1000));
    return diff;
}

function weekStatus(week: BootcampWeekSummary): WeekStatus {
    const p = week.progress;
    if (!p) return 'locked';
    if (p.unlocked) {
        if (p.pass_status === 'pass') return 'passed';
        if (p.pass_status === 'retry') return 'retry';
        if (p.pass_status === 'submitted') return 'submitted';
        if (p.prep_completed) return 'in_progress';
        return 'unlocked';
    }
    // Not unlocked yet — decide between "scheduled" and "locked".
    // Scheduled = cadence gate is in the future but the student is on
    // track (previous weeks passed). The backend stamps available_from
    // at benchmark-submit time, so the presence of a future
    // available_from implies the week is in the cadence queue rather
    // than hard-locked.
    const remaining = computeSecondsRemaining(p);
    if (remaining !== null && remaining > 0) {
        return 'scheduled';
    }
    return 'locked';
}

/** Render "Unlocks in 3 days 4h / tomorrow / in 22 minutes" given the
 *  progress row. The helper is stateless — live updates are driven by
 *  the ``nowTick`` re-render at the WeekRow level. */
function formatScheduledUnlock(
    progress: BootcampWeekSummary['progress'],
): string | null {
    const seconds = computeSecondsRemaining(progress);
    if (seconds === null || seconds <= 0) return null;
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (days >= 2) {
        return hours > 0 ? `Unlocks in ${days} days ${hours}h` : `Unlocks in ${days} days`;
    }
    if (days === 1) {
        return hours > 0 ? `Unlocks in 1 day ${hours}h` : `Unlocks tomorrow`;
    }
    if (hours >= 2) {
        return mins > 0 ? `Unlocks in ${hours}h ${mins}m` : `Unlocks in ${hours} hours`;
    }
    if (hours === 1) {
        return mins > 0 ? `Unlocks in 1h ${mins}m` : `Unlocks in 1 hour`;
    }
    const displayMins = Math.max(1, Math.ceil(seconds / 60));
    return `Unlocks in ${displayMins} minute${displayMins === 1 ? '' : 's'}`;
}

export default BootcampView;
